import { ISubset } from './message.interface';

export enum EMBEDDING_MESSAGE_TYPE {
  INIT = 'omm_cb_init',
  READY = 'omm_cb_ready',
}

export interface IQuestion {
  content: string;
  options: ISubset[];
}

export interface IUserInfoField {
  label: string;
  key: string;
  type: string;
  placeholder?: string;
  value?: any;
}

export interface IDetailsButton {
  tooltip: string;
  icon?: string;
  subset: ISubset;
}

export interface IClientOptions {
  ss?: string;
  ssw?: string;
  fq?: string;
  persona?: string;
  welcomeMessage?: string;
  initialQuestion?: IQuestion;
  sampleQuestions?: string[];
  inputPlaceholder?: string;
  sendButtonLabel?: string;
  assistant?: {
    name: string;
    avatar?: string;
  };
  queryUserInfo?: IUserInfoField[];
  showNewConversationButton?: boolean;
  cssUrl?: string;
  css?: string;
  requireEmbedding?: boolean;
  requiredReferrers?: string;
  /**
   * Allow opening the chat in a separate top-level browser window ("popout").
   * Shows an "open in new window" button while embedded. The popout is
   * authorized via a same-origin BroadcastChannel handshake from the opener
   * iframe, so it works even when `requireEmbedding` is on. Default: false.
   * Legacy v1 opens a fresh conversation in the popout (best-effort).
   */
  popout?: boolean;
  detailsButtons?: IDetailsButton[];
  agentId?: string;
  /** Theme version to apply to the chat interface. Use 'v2' for the new theme. */
  theme?: 'v1' | 'v2';
  /** Whether to show source pills below assistant messages. */
  showSourcePills?: boolean;
  /** Which public chat UI version to serve. 'legacy' keeps the old frontend/, 'v2' uses the new public-chat project. */
  public_chat_version?: 'legacy' | 'v2';
  /**
   * Optional Keycloak Identity Provider hint. When set, the login URL includes
   * `kc_idp_hint=<value>` so users are redirected directly to the external IDP.
   */
  kc_idp_hint?: string;
  /** Show thumbs-up / thumbs-down rating on assistant messages. Default: true. */
  showRating?: boolean;
  /** Custom HTML text shown to the user after a negative rating. Supports basic formatting. */
  negativeRatingText?: string;
  /** Whether to show the email input field after a negative rating. Default: true. */
  showEmailOnNegativeRating?: boolean;
}

export interface IClientOptionsForFrontend extends IClientOptions {
  ds: string;
  type?: 'chatbot' | 'analysis' | 'agent';
}

export interface IAccessConfig {
  title?: string;
  description?: string;
  showDescription?: boolean;
  deniedTitle?: string;
  deniedDescription?: string;
  css?: string;
  loginLabel?: string;
  registerLabel?: string;
  logoutLabel?: string;
  pendingTitle?: string;
  pendingDescription?: string;
  contactMail?: string;
  legalDocuments?: { [key: string]: { title?: string; content: string } };
}

/** Broad visibility level for an entity */
export type SharingScope = 'restricted' | 'tenant' | 'authenticated' | 'public';

export interface IAccessInfo {
  sharing_scope: SharingScope;
  public_config?: IAccessConfig;
  /** Which public chat version to serve — returned from server */
  public_chat_version: 'legacy' | 'v2';
  /** Optional Keycloak IDP hint for direct external IDP redirect */
  kc_idp_hint?: string;
  /**
   * Claract base URL when the dataset's tenant was migrated to Claract
   * (bd ai-hub-d10j.6); null/absent otherwise.
   */
  migrated_to?: string | null;
  /**
   * Claract id of a re-keyed (deduped) dataset, used in the redirect instead
   * of the legacy id; null when the dataset kept its id.
   */
  migrated_dataset_id?: string | null;
  /**
   * Only present when `migrated_to` is set: the dataset restricts embedding
   * pages (requiredReferrers), so the chat must not redirect to Claract.
   */
  has_required_referrers?: boolean;
}
